import { useEffect, useMemo, useState } from 'react';
import { Button, TableColumnsType, Tooltip, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { QuickRangePicker } from 'components/QuickRangePicker';
import ScrollTable from 'components/ScrollTable';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useAuthorization } from 'components/Authorization';
import { StoreInfo } from 'services/api/dashboard';
import { getHeaderLang } from 'services/api';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useStoreFilter from '../useStoreFilter';
import useModal from 'hooks/useModal';
import ConversationModal from 'views/Setting/Remind/components/ConversationModal';
import { exportSpsAftersales, getSpsAftersales } from './service';
import { DEFAULT_PAGE_SIZE, FETCH_ERROR_PHRASE } from './constants';
import { SpsConversation, useOpenSpsChat } from './useOpenSpsChat';
import StatusButton from 'components/StatusButton';

type Row = Dashboard.TikTokUS.AftersalesRow;

export interface SpsAftersalesDetailProps {
  departments: Dashboard.Department[];
  /** 从排行榜 AHT 列【明细】下钻带入 */
  initStoreInfo?: StoreInfo;
}

/** 默认时间范围：近 7 天（与其它平台数据页一致，不含当天） */
const defaultRange = (): [Dayjs, Dayjs] => [
  dayjs().subtract(7, 'day').startOf('day'),
  dayjs().subtract(1, 'day').endOf('day'),
];

/** 时长是 decimal 小时数、不是时间点；为空表示官方没返回该段时长，**按「-」展示，不要按 0** */
const durationRender = (value?: number | null) =>
  value === null || value === undefined ? '-' : `${value}h`;

/**
 * 售后处理明细（AHT 专用）
 * - 筛选：店铺 + 时间；**没有**售后类型筛选器（aftersalesTypes 一律不传）
 * - 列按 PRD 4.5；**没有店铺列**，也不加导出才有的外部店铺ID / 买家昵称 / 外部买家id
 */
function SpsAftersalesDetail({ departments, initStoreInfo }: SpsAftersalesDetailProps) {
  const { t } = useTranslate();

  const exportable = useGetPermission()('导出平台数据');
  const auth = useAuthorization<Oversea.Token.Payload>();
  const systemLanguage = useSystemLanguageValue();
  const { run: onDownload } = useExportFile();

  const {
    selectedDepartments,
    departmentsSelect,
    storeType,
    storeTypeSelect,
    storeInfo,
    storeInfoSelect,
  } = useStoreFilter({ platform: 'TIKTOK_US', departments, initStoreInfo });

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(defaultRange());

  const dateRangeSelect = (
    <QuickRangePicker
      quickActions={[
        {
          key: 'last7days',
          label: t('remind.近x天', { x: 7 }),
          range: [
            dayjs().subtract(7, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
        {
          key: 'last30days',
          label: t('remind.近x天', { x: 30 }),
          range: [
            dayjs().subtract(30, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
      ]}
      value={dateRange}
      onChange={(range) => {
        const [start, end] = range || [];
        if (start && end) {
          setDateRange([start, end]);
        }
      }}
    />
  );

  const rangeParams = () => ({
    platform: 'TIKTOK_US' as const,
    departmentIds: selectedDepartments.map(({ id }) => id),
    storeType:
      storeType === 'CROSS'
        ? ('跨境' as const)
        : storeType === 'LOCAL'
        ? ('本土' as const)
        : undefined,
    storeIds: storeInfo.list?.map(({ id }) => Number(id)),
    // 用户选的年月日原样传，不做时区换算
    startDate: dateRange[0].format('YYYY-MM-DD'),
    endDate: dateRange[1].format('YYYY-MM-DD'),
  });

  const { loading, data, run } = useRequest(
    (page: { pageNum: number; pageSize: number } = { pageNum: 1, pageSize }) =>
      getSpsAftersales({ ...rangeParams(), ...page }),
    {
      manual: true,
      onError: () => message.error(t(FETCH_ERROR_PHRASE)),
    },
  );

  // 排序吃服务端默认（orderCreateTime 等四键降序），前端不重排
  useEffect(() => {
    run({ pageNum: 1, pageSize });
  }, [run, pageSize, selectedDepartments, storeType, storeInfo, dateRange]);

  const handleExport = () => {
    onDownload(() =>
      exportSpsAftersales({
        ...rangeParams(),
        language: getHeaderLang(systemLanguage),
        userId: auth?.payload?.id,
      }),
    );
  };

  const openChat = useOpenSpsChat();

  /** 「查看会话」用 Modal 内嵌展示（与同模块 CSAT 明细一致） */
  const {
    visible: convVisible,
    show: showConv,
    hide: hideConv,
    meta: convMeta,
  } = useModal<SpsConversation>();

  const columns = useMemo<TableColumnsType<Row>>(
    () => [
      { key: 'orderId', dataIndex: 'orderId', title: t('订单号'), width: 160 },
      {
        key: 'returnOrderId',
        dataIndex: 'returnOrderId',
        title: t('退货单号'),
        width: 160,
        // 官方可能不返回，此时是**空串**
        render: (value?: string) => value || '-',
      },
      {
        key: 'aftersalesTypeName',
        dataIndex: 'aftersalesTypeName',
        title: t('售后类型'),
        width: 140,
        render: (value?: string) => value || '-',
      },
      {
        key: 'approveDurationHours',
        dataIndex: 'approveDurationHours',
        title: t('审批时长(小时)'),
        width: 130,
        render: durationRender,
      },
      {
        key: 'inspectDurationHours',
        dataIndex: 'inspectDurationHours',
        title: t('质检时长(小时)'),
        width: 130,
        render: durationRender,
      },
      {
        key: 'totalHandleDurationHours',
        dataIndex: 'totalHandleDurationHours',
        title: t('总处理时长(小时)'),
        width: 140,
        render: durationRender,
      },
      {
        key: 'operate',
        title: t('操作'),
        width: 120,
        fixed: 'right',
        render: (_, record) =>
          record.orderId && record.externalStoreId && record.externalBuyerId ? (
            <StatusButton
              type="link"
              onClick={async () => {
                // 优先用 externalBuyerId 反查；为空时 hook 内自动回落到按订单号查
                const found = await openChat({
                  orderId: record.orderId,
                  externalStoreId: record.externalStoreId,
                  externalBuyerId: record.externalBuyerId,
                  buyerNick: record.buyerNick,
                  region: record.region,
                });
                if (found) {
                  showConv(found);
                }
              }}
            >
              {t('查看会话')}
            </StatusButton>
          ) : (
            '-'
          ),
      },
    ],
    [openChat, showConv, t],
  );

  return (
    <Container>
      <header>
        {departmentsSelect}
        {storeTypeSelect}
        {storeInfoSelect}
        <div className="date-range">{dateRangeSelect}</div>
      </header>
      <section>
        <header>
          <h3>{t('售后处理明细')}</h3>
          {exportable && (
            <Tooltip title={t('导出详细数据')}>
              <Button icon={<DownloadOutlined />} onClick={handleExport} />
            </Tooltip>
          )}
        </header>
        <div>
          <ScrollTable
            loading={loading}
            rowKey={(row) => `${row.orderId}-${row.returnOrderId}-${row.aftersalesTypeName}`}
            columns={columns}
            dataSource={data?.list}
            pagination={{
              current: data?.pageNum || 1,
              pageSize: data?.pageSize || pageSize,
              total: data?.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (x) => t('feedback.共x项', { x }),
              onChange: (pageNum, size) => {
                setPageSize(size);
                run({ pageNum, pageSize: size });
              },
            }}
          />
        </div>
      </section>

      {convMeta && (
        <ConversationModal visible={convVisible} onCancel={() => hideConv()} record={convMeta} />
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
    padding: 8px 16px;
    margin-bottom: 16px;
    margin-top: 1px;
    background: var(--fett-bg-panel);
    > .date-range {
      margin-left: auto;
    }
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    > header {
      flex: 0 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 56px;
      padding: 0 16px;
      margin-bottom: 1px;
      background: var(--fett-bg-panel);
      > h3 {
        margin-bottom: 0;
      }
    }
    > div {
      flex: 1 0 0;
      padding: 16px;
      background: var(--fett-bg-panel);
    }
  }
`;

export default SpsAftersalesDetail;
